//
// Copyright 2026 DXOS.org
//

/**
 * Class recipes shared by every binding (decision 8): plain functions fixed at build time, so a React and a Solid part
 * emit identical classes. Geometry and colour live in `theme/*.css`; recipes only name the rules.
 */

const FOCUS_RING = 'dx-focus-ring';

export const recipes = {
  accordion: () => 'dx-accordion',
  accordionItem: () => 'dx-accordion-item',
  accordionItemContent: () => 'dx-accordion-item-content',
  accordionItemIcon: () => 'dx-accordion-item-icon',
  accordionItemIndicator: () => 'dx-accordion-item-indicator',
  accordionItemText: () => 'dx-accordion-item-text',
  accordionItemTrigger: () => `dx-accordion-item-trigger ${FOCUS_RING}`,
  arrow: () => 'dx-arrow',
  arrowTip: () => 'dx-arrow-tip',
  avatar: () => 'dx-avatar',
  avatarFallback: () => 'dx-avatar-fallback',
  avatarImage: () => 'dx-avatar-image',
  banner: () => 'dx-banner',
  bannerBody: () => 'dx-banner-body',
  bannerTitle: () => 'dx-banner-title',
  block: () => 'dx-block',
  breadcrumb: () => 'dx-breadcrumb',
  breadcrumbCurrent: () => 'dx-breadcrumb-current',
  breadcrumbItem: () => 'dx-breadcrumb-item',
  breadcrumbLink: () => `dx-breadcrumb-link ${FOCUS_RING}`,
  breadcrumbList: () => 'dx-breadcrumb-list',
  breadcrumbSeparator: () => 'dx-breadcrumb-separator',
  button: () => `dx-control dx-button ${FOCUS_RING}`,
  cardBody: () => 'dx-card-body',
  cardClickable: () => `dx-card-clickable ${FOCUS_RING}`,
  cardDescription: () => 'dx-card-description',
  cardFooter: () => 'dx-card-footer',
  cardHeader: () => 'dx-card-header',
  cardLink: () => `dx-card-link ${FOCUS_RING}`,
  cardPoster: () => 'dx-card-poster',
  cardRoot: () => 'dx-card',
  cardRow: () => 'dx-card-row',
  cardRowContent: () => 'dx-card-row-content',
  cardRowMain: () => 'dx-card-row-main',
  cardRowTrailing: () => 'dx-card-row-trailing',
  cardSection: () => 'dx-card-section',
  cardSectionTitle: () => 'dx-card-section-title',
  cardTile: () => 'dx-card-tile',
  cardTileClickable: () => `dx-card-tile-clickable ${FOCUS_RING}`,
  cardTitle: () => 'dx-card-title',
  carousel: () => 'dx-carousel',
  carouselCaption: () => 'dx-carousel-caption',
  carouselIndicator: () => `dx-carousel-indicator ${FOCUS_RING}`,
  carouselIndicatorGroup: () => 'dx-carousel-indicator-group',
  carouselItem: () => 'dx-carousel-item',
  carouselItemGroup: () => 'dx-carousel-item-group',
  carouselNextTrigger: () => 'dx-carousel-next-trigger',
  carouselPrevTrigger: () => 'dx-carousel-prev-trigger',
  checkbox: () => 'dx-checkbox',
  checkboxControl: () => 'dx-checkbox-control',
  collapsible: () => 'dx-collapsible',
  collapsibleContent: () => 'dx-collapsible-content',
  collapsibleIndicator: () => 'dx-collapsible-indicator',
  collapsibleTrigger: () => `dx-collapsible-trigger ${FOCUS_RING}`,
  combobox: () => 'dx-combobox',
  comboboxControl: () => 'dx-control dx-combobox-control',
  comboboxEmpty: () => 'dx-combobox-empty',
  comboboxFrame: () => 'dx-combobox-frame',
  comboboxInput: () => 'dx-combobox-input',
  comboboxItem: () => 'dx-select-item dx-combobox-item',
  comboboxItemDescription: () => 'dx-combobox-item-description',
  comboboxList: () => 'dx-combobox-list',
  comboboxSearch: () => 'dx-combobox-search',
  comboboxTrigger: () => 'dx-combobox-trigger',
  container: () => 'dx-container',
  controlFrame: () => 'dx-control dx-input dx-input-row dx-control-frame',
  controlRoot: () => 'dx-control-root',
  dateCalendar: () => 'dx-date-calendar',
  dateCalendarCell: () => `dx-date-calendar-cell ${FOCUS_RING}`,
  dateCalendarHeader: () => 'dx-date-calendar-header',
  dateCalendarTable: () => 'dx-date-calendar-table',
  dateCalendarTitle: () => 'dx-date-calendar-title',
  dateCalendarView: () => 'dx-date-calendar-view',
  dateField: () => 'dx-date-field',
  dateInput: () => 'dx-control dx-input dx-input-row dx-date-input',
  dateInputSegment: () => 'dx-date-input-segment',
  dateInputSegmentGroup: () => 'dx-date-input-segment-group',
  dateInputSegments: () => 'dx-date-input-segments',
  dialogBackdrop: () => 'dx-dialog-backdrop',
  dialogBody: () => 'dx-dialog-body',
  dialogContent: () => 'dx-dialog',
  dialogDescription: () => 'dx-dialog-description',
  dialogFooter: () => 'dx-dialog-footer',
  dialogHeader: () => 'dx-dialog-header',
  dialogPositioner: () => 'dx-dialog-positioner',
  dialogTitle: () => 'dx-dialog-title',
  dragPreview: () => 'dx-drag-preview',
  dropIndicator: () => 'dx-drop-indicator',
  editable: () => 'dx-editable',
  editableInput: () => `dx-control dx-input dx-editable-input ${FOCUS_RING}`,
  editablePreview: () => `dx-control dx-editable-preview ${FOCUS_RING}`,
  editablePreviewIcon: () => 'dx-editable-preview-icon',
  editablePreviewText: () => 'dx-editable-preview-text',
  editableSubmit: () => 'dx-editable-submit',
  empty: () => 'dx-empty',
  emptyText: () => 'dx-empty-text',
  errorFallback: () => 'dx-error-fallback',
  errorFallbackData: () => 'dx-error-fallback-data',
  errorFallbackMessage: () => 'dx-error-fallback-message',
  errorFallbackSection: () => 'dx-error-fallback-section',
  errorFallbackTitle: () => 'dx-error-fallback-title',
  errorStack: () => 'dx-error-stack',
  errorStackFile: () => 'dx-error-stack-file',
  errorStackFrame: () => 'dx-error-stack-frame',
  errorStackFunction: () => 'dx-error-stack-function',
  errorStackLine: () => 'dx-error-stack-line',
  errorStackLink: () => `dx-error-stack-function ${FOCUS_RING}`,
  field: () => 'dx-field',
  fieldError: () => 'dx-field-error',
  fieldHeader: () => 'dx-field-header',
  fieldHelper: () => 'dx-field-helper',
  fieldRequired: () => 'dx-field-required',
  fieldsetLegend: () => 'dx-fieldset-legend',
  fieldsetRoot: () => 'dx-fieldset',
  floatingPanelBody: () => 'dx-floating-panel-body',
  floatingPanelContent: () => `dx-floating-panel ${FOCUS_RING}`,
  floatingPanelControl: () => 'dx-floating-panel-control',
  floatingPanelDragTrigger: () => 'dx-floating-panel-drag-trigger',
  floatingPanelHeader: () => 'dx-floating-panel-header',
  floatingPanelResizeTrigger: () => 'dx-floating-panel-resize-trigger',
  floatingPanelTitle: () => 'dx-floating-panel-title',
  focusGroup: () => 'dx-focus-group',
  focusItem: () => 'dx-focus-item',
  group: () => 'dx-group',
  hoverCardContent: () => 'dx-hover-card',
  icon: () => 'dx-icon',
  image: () => 'dx-image',
  imageClickable: () => 'dx-image-clickable',
  input: () => `dx-control dx-input ${FOCUS_RING}`,
  inputAdornment: () => 'dx-input-adornment',
  inputField: () => 'dx-input-field',
  inputPrefix: () => 'dx-input-prefix',
  label: () => 'dx-label',
  link: () => `dx-link ${FOCUS_RING}`,
  listbox: () => 'dx-listbox',
  listboxContent: () => 'dx-listbox-content',
  listboxItem: () => 'dx-listbox-item',
  listboxItemDescription: () => 'dx-listbox-item-description',
  listboxItemIcon: () => 'dx-listbox-item-icon',
  listboxItemIndicator: () => 'dx-listbox-item-indicator',
  listboxItemText: () => 'dx-listbox-item-text',
  listboxScroll: () => 'dx-listbox-scroll',
  listboxTile: () => 'dx-listbox-tile',
  mainContent: () => 'dx-main-content',
  mainDrawer: () => 'dx-main-drawer',
  mainDrawerHandle: () => 'dx-main-drawer-handle',
  mainOverlay: () => 'dx-main-overlay',
  mainSidebar: () => 'dx-main-sidebar',
  mainSwipeArea: () => 'dx-main-swipe-area',
  mediaPlayer: () => 'dx-media-player',
  menuContent: () => 'dx-menu',
  menuIndicator: () => 'dx-menu-indicator',
  menuItem: () => 'dx-menu-item',
  menuItemText: () => 'dx-menu-item-text',
  menuShortcut: () => 'dx-menu-shortcut',
  numberInput: () => 'dx-control dx-input dx-input-row dx-number-input',
  panel: () => 'dx-panel',
  panelBody: () => 'dx-panel-body',
  panelFooter: () => 'dx-panel-footer',
  panelHeader: () => 'dx-panel-header',
  passwordInput: () => 'dx-control dx-input dx-input-row dx-password-input',
  pinInput: () => 'dx-control-root dx-pin-input-root',
  pinInputCell: () => `dx-control dx-input dx-pin-input-cell ${FOCUS_RING}`,
  pinInputControl: () => 'dx-pin-input',
  popoverBody: () => 'dx-popover-body',
  popoverContent: () => 'dx-popover',
  popoverDescription: () => 'dx-popover-description',
  popoverHeader: () => 'dx-popover-header',
  popoverTitle: () => 'dx-popover-title',
  popup: () => 'dx-popup',
  popupGroupLabel: () => 'dx-popup-group-label',
  progress: () => 'dx-progress',
  progressRange: () => 'dx-progress-range',
  progressTrack: () => 'dx-progress-track',
  qrCode: () => 'dx-qr-code',
  qrCodeFrame: () => 'dx-qr-code-frame',
  qrCodeOverlay: () => 'dx-qr-code-overlay',
  qrCodePattern: () => 'dx-qr-code-pattern',
  row: () => 'dx-row',
  scrollContainer: () => 'dx-scroll-container',
  scrollContainerFade: () => 'dx-scroll-container-fade',
  scrollContainerScrollDown: () => 'dx-scroll-container-scroll-down',
  scrollRoot: () => 'dx-scroll-root',
  scrollViewport: () => 'dx-scroll-viewport',
  selectItem: () => 'dx-select-item',
  selectTrigger: () => `dx-control dx-select-trigger ${FOCUS_RING}`,
  selectValueSizer: () => 'dx-select-value-sizer',
  separator: () => 'dx-separator',
  skeleton: () => 'dx-skeleton',
  slider: () => 'dx-slider',
  sliderControl: () => 'dx-slider-control',
  sliderLabel: () => 'dx-slider-label',
  sliderRange: () => 'dx-slider-range',
  sliderThumb: () => `dx-slider-thumb ${FOCUS_RING}`,
  sliderTrack: () => 'dx-slider-track',
  splitter: () => 'dx-splitter',
  splitterDivider: () => 'dx-splitter-divider',
  splitterPanel: () => 'dx-splitter-panel',
  splitterResizeTrigger: () => 'dx-splitter-resize-trigger',
  steps: () => 'dx-steps',
  stepsButton: () => `dx-steps-indicator dx-steps-button ${FOCUS_RING}`,
  stepsFill: () => 'dx-steps-fill',
  stepsIndicator: () => 'dx-steps-indicator',
  stepsItem: () => 'dx-steps-item',
  stepsSeparator: () => 'dx-steps-separator',
  submenuContent: () => 'dx-submenu',
  switch: () => 'dx-switch',
  switchControl: () => 'dx-switch-control',
  switchThumb: () => 'dx-switch-thumb',
  tabs: () => 'dx-tabs',
  tabsContent: () => `dx-tabs-content ${FOCUS_RING}`,
  tabsIndicator: () => 'dx-tabs-indicator',
  tabsList: () => 'dx-tabs-list',
  tabsTrigger: () => 'dx-tabs-trigger',
  tag: () => 'dx-tag',
  tagClickable: () => `dx-tag-clickable ${FOCUS_RING}`,
  tagDeleteTrigger: () => `dx-tag-delete-trigger ${FOCUS_RING}`,
  tagTrigger: () => `dx-tag-trigger ${FOCUS_RING}`,
  textarea: () => `dx-control dx-input dx-textarea ${FOCUS_RING}`,
  textCrawl: () => 'dx-text-crawl',
  textCrawlLine: () => 'dx-text-crawl-line',
  textCrawlRibbon: () => 'dx-text-crawl-ribbon',
  textTooltip: () => 'dx-text-tooltip',
  timestamp: () => 'dx-timestamp',
  toast: () => 'dx-popup dx-toast',
  toastCloseTrigger: () => 'dx-toast-close-trigger',
  toastCountdown: () => 'dx-toast-countdown',
  toastDescription: () => 'dx-toast-description',
  toaster: () => 'dx-toaster',
  toastFooter: () => 'dx-toast-footer',
  toastHeader: () => 'dx-toast-header',
  toastIcon: () => 'dx-toast-icon',
  toastTitle: () => 'dx-toast-title',
  toggleGroup: () => 'dx-toggle-group',
  toolbar: () => 'dx-toolbar',
  toolbarGap: () => 'dx-toolbar-gap',
  toolbarLink: () => `dx-toolbar-link ${FOCUS_RING}`,
  toolbarScroll: () => 'dx-toolbar-scroll',
  toolbarText: () => 'dx-toolbar-text',
  tooltipContent: () => 'dx-tooltip',
  tourBackdrop: () => 'dx-tour-backdrop',
  tourContent: () => 'dx-tour',
  tourControl: () => 'dx-tour-control',
  tourDescription: () => 'dx-tour-description',
  tourHeader: () => 'dx-tour-header',
  tourPositioner: () => 'dx-tour-positioner',
  tourProgressText: () => 'dx-tour-progress-text',
  tourSpotlight: () => 'dx-tour-spotlight',
  tourTitle: () => 'dx-tour-title',
  typography: () => 'dx-typography',
} as const;
